import { useEffect, useState, type JSX } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Icon } from '../../components/Icon';
import { NumberField } from '../../components/NumberField';
import { useProject, useRuntime, useUiContext } from '../../context';
import { attempt } from '../../safe';
import { TransitionBar } from './TransitionBar';
import '../panels.css';

export function TransitionsPanel(): JSX.Element {
  const { studio, ui } = useUiContext();
  const { commands, registry } = studio;
  const studioMode = useRuntime((r) => r.studioMode);
  const order = useProject(useShallow((p) => p.transitionOrder));
  const names = useProject(useShallow((p) => p.transitionOrder.map((id) => p.transitions[id]?.name ?? id)));
  const currentId = useProject((p) => p.currentTransitionId);
  const currentKind = useProject((p) => p.transitions[p.currentTransitionId]?.kind);
  const currentName = useProject((p) => p.transitions[p.currentTransitionId]?.name ?? '');
  const duration = useProject((p) => p.transitionDurationMs);
  // The command has no way to merge keystrokes into one undo step, so the field is written when it is left.
  const [durationDraft, setDurationDraft] = useState(duration);
  useEffect(() => setDurationDraft(duration), [duration]);

  const def = currentKind ? registry.getTransition(currentKind) : undefined;
  const instant = def?.fragment === null;
  let hasProperties = false;
  try {
    hasProperties = !!def && def.properties(def.defaults()).length > 0;
  } catch {
    hasProperties = false;
  }

  const openAddMenu = (anchor: HTMLElement): void => {
    const rect = anchor.getBoundingClientRect();
    ui.getState().openMenu({
      x: rect.left,
      y: rect.bottom + 2,
      returnFocus: anchor,
      items: registry.listTransitions().map((t) => ({
        label: t.displayName,
        onSelect: () => {
          const id = attempt(studio, () => commands.addTransition(t.kind));
          if (id) attempt(studio, () => commands.setCurrentTransition(id));
        },
      })),
    });
  };

  return (
    <div className="ui-panel">
      <div className="ui-panel-body transitions">
        <div className="ui-row">
          <select
            className="ui-select ui-grow"
            aria-label="Current transition"
            value={currentId}
            onChange={(e) => attempt(studio, () => commands.setCurrentTransition(e.target.value))}
          >
            {order.map((id, index) => (
              <option key={id} value={id}>
                {names[index]}
              </option>
            ))}
          </select>
          <button type="button" className="ui-icon-btn" aria-label="Add transition" aria-haspopup="menu" title="Add transition" onClick={(e) => openAddMenu(e.currentTarget)}>
            <Icon name="plus" />
          </button>
          <button
            type="button"
            className="ui-icon-btn"
            aria-label="Remove transition"
            title="Remove transition"
            disabled={order.length <= 1}
            onClick={() => attempt(studio, () => commands.removeTransition(currentId))}
          >
            <Icon name="minus" />
          </button>
          <button
            type="button"
            className="ui-icon-btn"
            aria-label="Rename transition"
            title="Rename transition"
            onClick={() =>
              ui.getState().openDialog({
                type: 'prompt',
                title: 'Rename transition',
                label: 'Name',
                initial: currentName,
                onSubmit: (name) => void attempt(studio, () => commands.updateTransition(currentId, { name })),
              })
            }
          >
            <Icon name="text" />
          </button>
          <button
            type="button"
            className="ui-icon-btn"
            aria-label="Transition properties"
            title="Transition properties"
            disabled={!hasProperties}
            onClick={() => ui.getState().openDialog({ type: 'transition-properties', transitionId: currentId })}
          >
            <Icon name="gear" />
          </button>
        </div>
        <div className="ui-row">
          <label htmlFor="transition-duration" className="ui-dim ui-grow">
            Duration
          </label>
          <NumberField
            id="transition-duration"
            label="Transition duration in milliseconds"
            value={durationDraft}
            min={0}
            max={20000}
            step={50}
            unit="ms"
            precision={0}
            disabled={instant}
            onChange={setDurationDraft}
            onCommit={() => {
              if (durationDraft !== duration) attempt(studio, () => commands.setTransitionDuration(durationDraft));
            }}
          />
        </div>
        {studioMode ? (
          <TransitionBar />
        ) : (
          <p className="ui-dim">Scene changes use this transition. Enable Studio Mode for the Transition button, quick transitions and the T-bar.</p>
        )}
      </div>
    </div>
  );
}
